import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Divider,
  Tab,
  Tabs,
  Grid,
  Stack,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import useTitle from "hooks/useTitle";
import { getMcaMasterData } from "api/services/mca/mca";
import { getMcaV3Credentials } from "api/services/organization";
import { ResType } from "types";
import AddMcaV3CredGlasse from "views/settings/organization/OrganizationProfile/Mca/AddMcaV3CredGlasse";
import { alerttt } from "assets";
import { useNavigate } from "react-router-dom";

const McaMasterData = () => {
  useTitle("ATOM Pro | MCA ");
  const [tab, setTab] = useState(0);
  const [cin, setCin] = useState("");
  const [searchCin, setSearchCin] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery(
    ["get-mca-filing-status", { cin: searchCin }],
    getMcaMasterData,
    {
      enabled: !!searchCin,
      retry: 1,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );



  const formatDate = (date?: string) => {
    if (!date) return "-";

    const parsed = new Date(date);

    if (isNaN(parsed.getTime())) return "-";

    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount?: string | number) => {
    if (!amount) return "₹ 0";

    return `₹ ${Number(amount).toLocaleString("en-IN")}`;
  };
  const getAddressByType = (type: string) => {
    const addr = masterData?.MCAMDSCompanyAddress?.find(
      (a: any) => a.addressType === type
    );

    if (!addr) return "-";

    return [
      addr.streetAddress,
      addr.streetAddress2,
      addr.locality,
      addr.city,
      addr.district,
      addr.state,
      addr.country,
      addr.postalCode,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const masterData = useMemo(() => {
    if (!data?.success) return null;
    return data.master_data;
  }, [data]);

  const directors = useMemo(() => {
    if (!data?.success || !Array.isArray(data.directors)) return [];

    const map = new Map();

    data.directors.forEach((dir: any) => {
      const din = (dir.DIN || "").trim();
      const pan = (dir.PAN || "").trim();

      const name =
        `${dir.FirstName || ""} ${dir.MiddleName || ""} ${dir.LastName || ""}`
          .replace(/\s+/g, " ")
          .trim();

      const roles = dir.MCAUserRole || [];

      const designations = roles
        .map((r: any) => r.roleLICValue || r.designation)
        .filter(Boolean);

      if (!name || designations.length === 0) return;

      const key = din
        ? `DIN_${din}`
        : pan
          ? `PAN_${pan}`
          : null;

      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          dinPan: din || pan,
          name,
          designationSet: new Set(designations),
          original: dir,
        });
      } else {
        const existing = map.get(key);
        designations.forEach((d: string) =>
          existing.designationSet.add(d)
        );
      }
    });

    return Array.from(map.values()).map((item, index) => {
      const roles = item.original.MCAUserRole || [];

      let designation = "-";

      const roleValues = roles
        .map((r: any) => r.roleLICValue || r.designation)
        .filter(Boolean);

      if (
        roleValues.some((r: string) =>
          r.toLowerCase().includes("director")
        )
      ) {
        designation = "Director";
      } else if (
        roleValues.some((r: string) =>
          r.toLowerCase().includes("ceo")
        )
      ) {
        designation = "CEO";
      }

      const uniqueCategory =
        (Array.from(
          new Set(
            roles
              .map((r: any) => r.directorCategory)
              .filter(Boolean)
          )
        )[0] as string) || "-";

      return {
        srNo: index + 1,
        dinPan: item.dinPan,
        name: item.name,
        designation,
        category: uniqueCategory,
        appointmentDate: item.original.dateOfAppointment || "-",
        cessationDate:
          roles.find((r: any) => r.cessationDate)?.cessationDate || "-",
        email:
          roles.find((r: any) => r.occupation)?.occupation || "-",
        occupation: (() => {
          const value = roles.find((r: any) => r.occupation)?.occupation;
          return value && value.includes("@") ? value : "-";
        })(),

        userName: (() => {
          const value = roles.find((r: any) => r.userName)?.userName;
          return value && value.includes("@") ? value : "-";
        })(),
      };
    });
  }, [data]);

  const getErrorMessage = () => {
    const backendMessage =
      data?.error?.message ||
      data?.message ||
      (error as any)?.message ||
      "";

    const normalizedMessage = backendMessage.toLowerCase();

    if (
      normalizedMessage.includes("500") ||
      normalizedMessage.includes("internal server error")
    ) {
      return "Something Went Wrong, Please Try Again Later";
    }

    return backendMessage || "Failed to fetch MCA data. Try again later.";
  };


  const handleSearch = () => {
    setSearchCin(cin.trim());
    setPage(0);
    refetch();
  };


  // const { data: mcaCredentials, isLoading: mcaCredentialsLoading }: ResType = useQuery(
  //   "org-mca-credentials",
  //   getMcaV3Credentials
  // );
  // const isMcaConnected = (mcaCredentials?.data?.mcaV3UserId && mcaCredentials?.data?.mcaV3Password) ? true : false;

  const isMcaConnected = true ;


  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: { xs: 2, md: 4 },
        py: 4,
        background: "linear-gradient(135deg, #eef2ff, #f8fafc)",
      }}
    >
      {/* Header */}
      <Box textAlign="center" mb={5}>
        <Typography variant="h5" fontWeight={700}>
          MCA Master Data
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Search company master data and director details using CIN
        </Typography>
      </Box>



      {/* {!isMcaConnected && (
        <Grid item xs={12} mb={4}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "22px",
              position: "relative",
              overflow: "hidden",
              color: "#fff",

              background: "linear-gradient(135deg, #9b3d02 0%, #9c3a00 100%)",

              boxShadow: `
                                0 15px 40px rgba(156,58,0,0.45)
                            `,

              border: "1px solid rgba(255,140,0,0.35)",

              transition: "all 0.25s ease",

              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: `
                                    0 20px 50px rgba(156,58,0,0.55)
                                `,
              },
            }}
          >

            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
              spacing={3}
            >
              <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    background: "rgba(255, 255, 255, 0.08)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    border: "1px solid rgba(255,255,255,0.18)",

                    boxShadow: `
                        0 6px 20px rgba(0,0,0,0.12),
                        inset 0 1px 0 rgba(255,255,255,0.35)
                    `,
                  }}
                >
                  <Box
                    component="img"
                    src={alerttt}
                    alt="MCA Logo"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    MCA V3 Portal Not Connected
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      opacity: 0.85,
                      maxWidth: 650,
                      lineHeight: 1.6,
                    }}
                  >
                    To use MCA API services and file forms directly to the portal,
                    you need to add your MCA V3 login credentials. This is a
                    one-time secure setup with OTP verification.
                  </Typography>

                  <Button
                    variant="contained"
                    sx={{
                      mt: 2.5,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #3b82f6, #2563eb)",
                      boxShadow: "0 6px 20px rgba(37,99,235,0.45)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 24px rgba(37,99,235,0.6)",
                      },
                      transition: "all 0.25s ease",
                    }}
                    onClick={() => setOpen(true)}
                  >
                    🔐 Add MCA Credentials Now
                  </Button>
                  <AddMcaV3CredGlasse open={open} setOpen={setOpen} />
                </Box>
              </Stack>

              <Button
                variant="outlined"
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.35)",
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "10px",
                  px: 3,
                  backdropFilter: "blur(6px)",
                  background: "rgba(255,255,255,0.05)",
                  "&:hover": {
                    background: "rgba(255,255,255,0.15)",
                    borderColor: "#fff",
                  },
                }}
                onClick={() => navigate("/settings/organization-profile")}
              >
                Go to Settings →
              </Button>
            </Stack>
          </Paper>
        </Grid>
      )} */}

     {isMcaConnected && (
      <Paper
        sx={{
          p: 4,
          mb: 5,
          borderRadius: "20px",
          background: "rgba(255,255,255,0.25)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          border: "1px solid rgba(255,255,255,0.4)",
          boxShadow: `
          0 12px 40px rgba(0,0,0,0.08),
          inset 0 1px 2px rgba(255,255,255,0.6)
        `,
        }}
      >
        <Box
          display="flex"
          gap={2}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          <TextField
            fullWidth
            label="Enter CIN Number"
            value={cin}
            onChange={(e) =>
              setCin(e.target.value.toUpperCase())
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                background: "rgba(255,255,255,0.6)",
                backdropFilter: "blur(8px)",
              },
            }}
          />

          <Button
            variant="contained"
            size="large"
            onClick={handleSearch}
            disabled={!cin.trim() || isLoading || isFetching}
            sx={{
              px: 4,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 600,
              color: "#fff",

              background: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.25)",

              boxShadow: `
              0 6px 20px rgba(0,0,0,0.4),
              inset 0 1px 1px rgba(255,255,255,0.2)
            `,
              // transition: "all 0.35s ease",

              "&:hover": {
                background: "rgba(37, 99, 235, 0.75)",
                transform: "translateY(-2px)",
                boxShadow: `
                0 12px 30px rgba(37,99,235,0.6),
                inset 0 1px 2px rgba(255,255,255,0.5)
              `,
              },
            }}
          >
            Search
          </Button>
        </Box>
      </Paper>
      )}

      {isLoading || isFetching ? (
        <Box textAlign="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : isError || data?.success === false ? (
        <Box
          sx={{
            mt: 3,
            p: 3,
            borderRadius: "20px",
            background: "linear-gradient(135deg, #ffe5e5, #fff5f5)",
            border: "1px solid #ffcccc",
            boxShadow: "0 10px 25px rgba(255, 0, 0, 0.08)",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 45,
              height: 45,
              borderRadius: "12px",
              background: "#ff4d4f",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "20px",
              fontWeight: 700,
            }}
          >
            !
          </Box>

          <Box>
            <Typography fontWeight={700} color="#b00020">
              Oops! Something went wrong
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {getErrorMessage()}
            </Typography>
          </Box>
        </Box>
      ) : masterData ? (
        <Paper
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            background: "rgba(255,255,255,0.25)",
            backdropFilter: "blur(18px)",
            border: "1px solid rgba(255,255,255,0.4)",
            boxShadow: `
        0 15px 45px rgba(0,0,0,0.08),
        inset 0 1px 2px rgba(255,255,255,0.6)
      `,
          }}
        >
          <Tabs
            value={tab}
            onChange={(e, newValue) => setTab(newValue)}
            sx={{
              px: 3,
              backdropFilter: "blur(10px)",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
              },
              "& .Mui-selected": {
                color: "#2563eb",
              },
            }}
          >
            <Tab label="Master Data" />
            <Tab label="Directors Details" />
          </Tabs>

          <Divider sx={{ opacity: 0.4 }} />

          {/* MASTER TAB */}
          {tab === 0 && (
            <Box p={4}>
              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  sm: "250px 1fr",
                }}
                rowGap={2}
                columnGap={3}
              >
                {[
                  ["CIN", masterData.CIN],
                  ["Company Name", masterData.company],
                  ["ROC Name", masterData.rocName],
                  ["Registration Number", masterData.registrationNumber],
                  ["Date of Incorporation", formatDate(masterData.dateOfIncorporation)],
                  ["Email", masterData.emailAddress],
                  ["Registered Address", getAddressByType("Registered Address")],
                  ["Books of Account Address", getAddressByType("Book of Account")],
                  ["Listed in Stock Exchange(S) (Y/N)", masterData.whetherListedOrNot === "Y" ? "Yes" : "No"],
                  ["Category of Company", masterData.companyCategory],
                  ["Subcategory of the Company", masterData.companySubcategory],
                  ["Class of Company", masterData.classOfCompany],
                  ["ACTIVE compliance", masterData.activeCompliance],//inc22Aflag
                  ["Authorised Capital (Rs)", formatCurrency(masterData.authorisedCapital)],
                  ["Paid Up Capital (Rs)", formatCurrency(masterData.paidUpCapital)],
                  ["Date of Last AGM", formatDate(masterData.dateOfLastAGM)],
                  ["Balance Sheet Date", formatDate(masterData.balanceSheetDate)],
                  ["Company Status", masterData.llpStatus],
                  ["Small company", masterData.smallCompanyFlag === "Y" ? "Yes" : "No"],
                ].map(([label, value], i) => (
                  <Box key={i} display="contents">
                    <Typography fontWeight={600}>{label}</Typography>
                    <Typography>{value || "-"}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* DIRECTORS TAB */}
          {tab === 1 && (
            <Box p={4}>
              {directors.length === 0 ? (
                <Alert
                  severity="info"
                  sx={{ borderRadius: "14px" }}
                >
                  No Director records found.
                </Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          "S.No",
                          "DIN/PAN",
                          "Name",
                          "Designation",
                          "Category",
                          "Appointment Date",
                          "Cessation Date",
                          "Email",
                        ].map((header, i) => (
                          <TableCell
                            key={i}
                            sx={{
                              fontWeight: 700,
                              background: "rgba(255,255,255,0.6)",
                              backdropFilter: "blur(6px)",
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {directors.map((row) => (
                        <TableRow
                          key={row.dinPan}
                          hover
                          sx={{
                            transition: "0.3s",
                            "&:hover": {
                              background:
                                "rgba(37,99,235,0.08)",
                            },
                          }}
                        >
                          <TableCell>{row.srNo}</TableCell>
                          <TableCell>{row.dinPan}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.designation}</TableCell>
                          <TableCell>{row.category}</TableCell>
                          <TableCell>{formatDate(row.appointmentDate)}</TableCell>
                          <TableCell>{formatDate(row.cessationDate)}</TableCell>
                          <TableCell>{row.occupation || row.userName}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Paper>
      ) : null}



    </Box>
  );


};

export default McaMasterData;
